<script setup lang="ts">
/**
 * Блок GitLab — макет 13:461: заголовок и коробка со строками веток, merge
 * request'ов и поднятого стенда, у каждой — бейдж последнего пайплайна.
 *
 * Строки моноширинные: их читают как имена веток, а не как предложения, и
 * пропорциональный шрифт здесь мешает сравнивать «DEV-1421-kanban» с
 * «DEV-1421-kanban-2».
 *
 * Пока хоть один прогон не закончился, блок сам перечитывает себя: человек
 * запустил пайплайн из соседнего окна и ждёт зелёного, а не жмёт F5. Опрос
 * прекращается, когда всё досчиталось или карточку закрыли.
 *
 * Кнопки «Поднять стенд», «Флаг» и «Релиз» из шапки макета живут отдельным
 * компонентом: они не показывают состояние, а меняют его, и видны только с
 * правом на действия DevOps.
 */
import { GitBranch, GitMerge, Server } from 'lucide-vue-next'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import IssueDevActions from '@/features/devops/components/IssueDevActions.vue'

import { getDevInfo, hasLivePipeline, pipelineFor } from '../devinfo'
import { useIssueStore } from '../store'
import PipelineBadge from './PipelineBadge.vue'

import type { DevInfo, DevMergeRequest, DevPipeline } from '../devinfo'

/** Как часто перечитываем, пока что-то бежит */
const POLL_MS = 10_000

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t, te } = useI18n()
const store = useIssueStore()

const info = ref<DevInfo | null>(null)
let timer: ReturnType<typeof setTimeout> | null = null

const issueId = computed(() => store.issue?.id ?? '')

/** Состояние merge request'а словами: «Ревью», «Черновик», «Смержен», «Закрыт» */
const stateOf = (request: DevMergeRequest): { text: string; tone: string } => {
  if (request.draft) return { text: t('issue.dev.mr.draft'), tone: 'muted' }

  const key = `issue.dev.mr.${request.state}`

  return {
    text: te(key) ? t(key) : request.state,
    tone: request.state === 'opened' ? 'accent' : 'muted',
  }
}

interface DevRow {
  id: string
  kind: 'branch' | 'mr' | 'stand'
  name: string
  url: string
  state: { text: string; tone: string } | null
  pipeline: DevPipeline | null
}

/**
 * Строки идут парами «ветка и её merge request», как в макете: так видно, что
 * происходит с каждым репозиторием, а не отдельно список веток и отдельно
 * список MR. Merge request без своей ветки в зеркале — тоже строка: ветку
 * могли удалить после мержа.
 */
const rows = computed<DevRow[]>(() => {
  if (!info.value) return []

  const { branches, mergeRequests, pipelines, stands } = info.value
  const requestsOf = (projectPath: string, branch: string) =>
    mergeRequests.filter(
      (request) => request.projectPath === projectPath && request.sourceBranch === branch,
    )

  const mrRow = (request: DevMergeRequest): DevRow => ({
    id: `mr-${request.id}`,
    kind: 'mr',
    name: `!${request.iid} ${request.title}`,
    url: request.url,
    state: stateOf(request),
    pipeline: pipelineFor(pipelines, request.projectPath, request.sourceBranch),
  })

  const paired = new Set<string>()

  const branchRows = branches.flatMap((branch) => {
    const own = requestsOf(branch.projectPath, branch.name)

    own.forEach((request) => paired.add(request.id))

    return [
      {
        id: `br-${branch.id}`,
        kind: 'branch' as const,
        name: `${branch.projectPath}: ${branch.name}`,
        url: branch.url,
        state: null,
        pipeline: pipelineFor(pipelines, branch.projectPath, branch.name),
      },
      ...own.map(mrRow),
    ]
  })

  const orphanRows = mergeRequests.filter((request) => !paired.has(request.id)).map(mrRow)

  // Ссылка ведёт на пайплайн подъёма, а не на «https://<номер стенда>»: адресов
  // стенда журнал не знает, их показывает раздел «Фича-окружения».
  const standRows = stands.map((stand) => ({
    id: `st-${stand.id}`,
    kind: 'stand' as const,
    name: stand.name,
    url: stand.pipelineUrl,
    state: {
      text: stand.isRemoved ? t('issue.dev.stand.removed') : t('issue.dev.stand.live'),
      tone: stand.isRemoved ? 'muted' : 'accent',
    },
    pipeline: null,
  }))

  return [...branchRows, ...orphanRows, ...standRows]
})

const stopPolling = () => {
  if (timer) clearTimeout(timer)

  timer = null
}

const load = async (isPoll = false) => {
  if (!issueId.value) return

  try {
    const loaded = await getDevInfo(issueId.value)

    // Пока ходили, открыли соседнюю задачу — этот ответ уже не о ней.
    if (!issueId.value) return

    info.value = loaded

    stopPolling()

    if (hasLivePipeline(loaded.pipelines)) {
      timer = setTimeout(() => void load(true), POLL_MS)
    }
  } catch (failure) {
    // Отказ на опросе молчит: блок уже показан, и всплывающая ошибка раз в
    // десять секунд — худшее, что можно сделать с открытой карточкой.
    if (!isPoll) emit('failed', failure)

    stopPolling()
  }
}

onMounted(() => void load())

watch(issueId, () => {
  info.value = null
  stopPolling()
  void load()
})

onBeforeUnmount(stopPolling)
</script>

<template>
  <section v-if="rows.length" class="gitlab">
    <header class="gitlab__head">
      <h2 class="gitlab__title">{{ t('issue.dev.title') }}</h2>

      <span class="gitlab__spacer" />

      <IssueDevActions
        v-if="store.issue"
        :issue-id="store.issue.id"
        :issue-key="store.issue.key"
        :epic-id="store.issue.parentId"
        :stands="info?.stands ?? []"
        @done="load()"
        @failed="emit('failed', $event)"
      />
    </header>

    <div class="gitlab__box">
      <a v-for="row in rows" :key="row.id" class="glrow" :href="row.url" target="_blank" rel="noopener">
        <GitBranch v-if="row.kind === 'branch'" class="glrow__icon" :size="15" />
        <GitMerge v-else-if="row.kind === 'mr'" class="glrow__icon" :size="15" />
        <Server v-else class="glrow__icon" :size="15" />

        <span class="glrow__name">{{ row.name }}</span>

        <span
          v-if="row.state"
          class="glrow__state"
          :class="`glrow__state--${row.state.tone}`"
        >
          {{ row.state.text }}
        </span>

        <span class="glrow__spacer" />

        <PipelineBadge v-if="row.pipeline" :pipeline="row.pipeline" />
      </a>
    </div>
  </section>
</template>

<style scoped>
.gitlab {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gitlab__head {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 32px;
}

.gitlab__spacer {
  flex: 1;
  min-width: 0;
}

.gitlab__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-medium);
  line-height: 17px;
}

.gitlab__box {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
}

.glrow {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  height: 36px;
  padding: 0 14px;
  color: inherit;
  text-decoration: none;
}

.glrow + .glrow {
  border-top: 1px solid var(--color-border);
}

.glrow:hover {
  background: var(--color-bg-muted);
}

.glrow__icon {
  flex-shrink: 0;
  color: var(--color-text-muted);
}

.glrow__name {
  overflow: hidden;
  color: var(--color-text-primary);
  font-family: var(--font-family-mono);
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.glrow__state {
  flex-shrink: 0;
  font-size: var(--font-size-mono);
  font-weight: var(--font-weight-medium);
  line-height: 15px;
  white-space: nowrap;
}

.glrow__state--accent {
  color: var(--color-success-strong);
}

.glrow__state--muted {
  color: var(--color-text-muted);
}

.glrow__spacer {
  flex: 1;
  min-width: 0;
}
</style>
